import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/data/keys';
import {
  addFavorite,
  listMyFavorites,
  listPlayersByIds,
  recordMissionEvent,
  removeFavorite,
} from '@/lib/data/provider';
import type { FavoriteEntityType, Tables } from '@/lib/supabase/types';

type Favorite = Tables<'user_favorites'>;

/** All of the signed-in user's follows (teams + competitions + players). */
export function useFavorites() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: queryKeys.favorites(userId ?? 'anon'),
    queryFn: listMyFavorites,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

const matches = (favorite: Favorite, type: FavoriteEntityType, id: string) =>
  favorite.entity_type === type && favorite.entity_id === id;

/** Whether the given entity is currently followed (false while loading / guest). */
export function useIsFavorite(
  entityType: FavoriteEntityType,
  entityId: string | undefined,
): boolean {
  const { data } = useFavorites();
  if (!entityId || !data) return false;
  return data.some((favorite) => matches(favorite, entityType, entityId));
}

/** Followed players resolved to full rows (for the Following screen). */
export function useFollowedPlayers() {
  const favorites = useFavorites();
  const ids = (favorites.data ?? [])
    .filter((favorite) => favorite.entity_type === 'player')
    .map((favorite) => favorite.entity_id);
  return useQuery({
    queryKey: ['followed-players', [...ids].sort().join(',')],
    queryFn: () => listPlayersByIds(ids),
    enabled: isSupabaseConfigured && ids.length > 0,
    staleTime: QUERY_STALE_TIME.reference,
  });
}

interface ToggleVars {
  entityType: FavoriteEntityType;
  entityId: string;
  next: boolean;
}

/**
 * Follow / unfollow with an optimistic cache write so the star flips instantly.
 * Rolls back on failure and re-syncs from the server on settle.
 */
export function useToggleFavorite() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const key = queryKeys.favorites(userId ?? 'anon');

  return useMutation({
    mutationFn: async ({ entityType, entityId, next }: ToggleVars) => {
      if (!userId) throw new Error('NOT_AUTHENTICATED');
      if (next) {
        await addFavorite(userId, entityType, entityId);
      } else {
        await removeFavorite(entityType, entityId);
      }
    },
    onMutate: async ({ entityType, entityId, next }: ToggleVars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Favorite[]>(key);
      queryClient.setQueryData<Favorite[]>(key, (old) => {
        const list = old ?? [];
        if (next) {
          if (list.some((favorite) => matches(favorite, entityType, entityId))) return list;
          const optimistic: Favorite = {
            id: `optimistic-${entityType}-${entityId}`,
            user_id: userId ?? '',
            entity_type: entityType,
            entity_id: entityId,
            sort_order: 0,
            created_at: new Date().toISOString(),
          };
          return [optimistic, ...list];
        }
        return list.filter((favorite) => !matches(favorite, entityType, entityId));
      });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: (_data, error, vars) => {
      queryClient.invalidateQueries({ queryKey: key });
      if (!error && vars.next) {
        void recordMissionEvent('follow')
          .then(() => queryClient.invalidateQueries({ queryKey: ['rewards'] }))
          .catch(() => {});
      }
    },
  });
}
